import React, { useState, useEffect, useRef } from "react";
import { Box, Checkbox, FormControlLabel, TextField } from "@mui/material";
import { SupportedUserDataTypes } from "hooks/useUserDataKeys";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

interface UserfieldProps {
  name: string;
  value: unknown;
  onChange: (v: unknown) => void;
  type: SupportedUserDataTypes;
  readonly?: boolean;
  linkChange?: boolean;
}

const DATETIME_FORMAT = "DD-MM-YYYY hh:mm";

const Userfield: React.FC<UserfieldProps> = ({
  readonly,
  value: _value,
  linkChange = false,
  onChange,
  name,
  type,
}) => {
  const value = _value === "false" ? false : _value === "true" ? true : _value;
  const [internalValue, setInternalValue] = useState(value);
  const isEscaping = useRef(false);

  useEffect(() => {
    setInternalValue(value || "");
  }, [value]);

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (linkChange) return;

    if (e.key === "Enter") {
      onChange(internalValue);
      (e.target as HTMLElement).blur();
    } else if (e.key === "Escape") {
      isEscaping.current = true;
      setInternalValue(value || "");
      (e.target as HTMLElement).blur();
    }
  };

  const handleBlur = () => {
    if (linkChange) return;

    if (isEscaping.current) {
      isEscaping.current = false;
      return;
    }
    onChange(internalValue);
  };

  if (type === SupportedUserDataTypes.string) {
    return (
      <Box pt={1}>
        <TextField
          fullWidth
          label={name}
          value={(linkChange ? value : internalValue) || ""}
          onChange={(e) =>
            linkChange
              ? onChange(e.target.value)
              : setInternalValue(e.target.value)
          }
          onBlur={handleBlur}
          onKeyUp={handleKeyUp}
          size="small"
          disabled={readonly}
          id={`string-userschema-${name}`}
        />
      </Box>
    );
  } else if (type === SupportedUserDataTypes.number) {
    return (
      <Box pt={1}>
        <TextField
          onChange={(e) => {
            linkChange
              ? onChange(e.target.value)
              : setInternalValue(e.target.value);
          }}
          fullWidth
          label={name}
          value={(linkChange ? value : internalValue) || 0}
          onBlur={handleBlur}
          onKeyUp={handleKeyUp}
          size="small"
          type="number"
          disabled={readonly}
          id={`number-userschema-${name}`}
        />
      </Box>
    );
  } else if (type === SupportedUserDataTypes.boolean) {
    return (
      <Box pt={1}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={Boolean(value)}
              onChange={(e, checked) => {
                onChange(checked);
                e.stopPropagation();
              }}
              disabled={readonly}
              id={`boolean-userschema-${name}`}
            />
          }
          slotProps={{
            typography: {
              sx: {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            },
          }}
          sx={{ maxWidth: "100%", ml: 0, flex: 0 }}
          labelPlacement="start"
          label={name}
        />
      </Box>
    );
  } else if (type === SupportedUserDataTypes.date) {
    return (
      <Box pt={1}>
        <DateTimePicker
          value={value ? dayjs(value as string, DATETIME_FORMAT) : null}
          onChange={(value: dayjs.Dayjs | null) =>
            onChange(value?.format(DATETIME_FORMAT))
          }
          ampm={false}
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              label: name,
              id: `date-userschema-${name}`,
            },
          }}
          disabled={readonly}
        />
      </Box>
    );
  } else {
    return (
      <Box pt={1}>
        <TextField
          fullWidth
          label={name}
          value={(linkChange ? value : internalValue) || ""}
          onChange={(e) =>
            linkChange
              ? onChange(e.target.value)
              : setInternalValue(e.target.value)
          }
          onBlur={handleBlur}
          onKeyUp={handleKeyUp}
          size="small"
          disabled={readonly}
          id={`string-userschema-${name}`}
        />
      </Box>
    );
  }
};

export default Userfield;